import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    CanLoad,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthGuard } from './auth.guard';

@Injectable({
    providedIn: 'root',
})
export class TenantAuthGuard extends AuthGuard implements CanActivate, CanActivateChild, CanLoad {
    /**
     * Constructor
     */
    constructor(private authService: AuthService, private router: Router) {
        super(authService, router);
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     *
     * @param route
     * @param state
     */
    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this.check(redirectUrl);
    }

    /**
     * Can activate child
     *
     * @param childRoute
     * @param state
     */
    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this.check(redirectUrl);
    }

    /**
     * Can load
     *
     * @param route
     * @param segments
     */
    canLoad(
        route: Route,
        segments: UrlSegment[]
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this.check('/');
    }

    /**
     * Check the authenticated status
     *
     * @param redirectURL
     * @private
     */
    protected override check(redirectURL: string): Observable<boolean> {
        // Check the authentication status
        return super.check(redirectURL, false).pipe(
            // Here you can add custom logic
            // For example, additional checks or logging
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.authService.checkForTenant().pipe(
                        switchMap((tenantAuthenticated) => {
                            // If the tenant is not selected...
                            if (!tenantAuthenticated) {
                                // Redirect to the sign-in page
                                this.router.navigate(['sign-in'], {
                                    queryParams: { redirectURL },
                                });
                                // Prevent the access
                                return of(false);
                            }
                            // Allow the access
                            return of(true);
                        })
                    );
                }
                // Prevent the access
                return of(false);
            })
        );
    }
}
